import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobService from '../services/JobService';
import CompanyService from '../services/CompanyService';
import { fmtDate } from '../utils/format';
import { useCompanyLogo } from '../components/common/companyLogo';

const PRINT_CSS = `
@page { size: A4; margin: 16mm; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  .sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; width: auto !important; min-height: 0 !important; }
  [contenteditable] { outline: none !important; background: none !important; }
}
.sheet [contenteditable]:hover { background: rgba(66,120,152,0.08); }
.sheet [contenteditable]:focus { outline: 2px solid rgba(66,120,152,0.4); background: rgba(66,120,152,0.05); }
`;

/** Text the user can correct right on the sheet before printing. */
const Edit = ({ children, block, sx }) => (
    <Box component={block ? 'div' : 'span'} contentEditable suppressContentEditableWarning
         sx={{ borderRadius: '3px', px: 0.25, whiteSpace: 'pre-wrap', ...sx }}>
        {children}
    </Box>
);

const Row = ({ label, children }) => (
    <Box sx={{ display: 'flex', gap: 2, py: '7px', borderBottom: '1px solid #E5E7EB' }}>
        <Box sx={{ width: '34%', flexShrink: 0, color: '#4B5563', fontSize: 13 }}>{label}</Box>
        <Box sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: '#111827' }}><Edit>{children || ' '}</Edit></Box>
    </Box>
);

const SignLine = ({ label }) => (
    <Box sx={{ flex: 1, minWidth: 200 }}>
        <Box sx={{ borderBottom: '1px solid #111827', height: 56 }} />
        <Typography sx={{ fontSize: 12, color: '#4B5563', mt: 0.75, textAlign: 'center' }}>{label}</Typography>
    </Box>
);

/**
 * A4 forms printed from a job and signed on paper: the site-visit certificate (building staking)
 * and the client's receipt of the survey report (building survey). Every value is editable on screen.
 */
export default function PrintFormPage({ kind }) {
    const { id, visitId } = useParams();
    const { t } = useTranslation();
    const logo = useCompanyLogo();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        Promise.all([
            JobService.get(id),
            CompanyService.get(),
            kind === 'visit' ? JobService.visits(id) : Promise.resolve([]),
        ]).then(([job, company, visits]) => {
            const visit = kind === 'visit' ? visits.find(v => String(v.id) === String(visitId)) : null;
            if (kind === 'visit' && !visit) throw new Error('visit');
            setData({ job, company, visit });
        }).catch(() => setError(true));
    }, [id, visitId, kind]);

    const toolbar = (
        <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', maxWidth: '210mm', mx: 'auto', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/jobs/${id}`)} sx={{ color: '#64748B' }}>{t('common.back')}</Button>
            <Typography sx={{ flex: 1, fontSize: 13, color: '#64748B', minWidth: 200 }}>{data ? t('print.editHint') : ''}</Typography>
            <Button variant="contained" startIcon={<PrintOutlinedIcon />} disabled={!data} onClick={() => window.print()}>{t('print.print')}</Button>
        </Box>
    );

    if (error || !data) {
        return (
            <Box sx={{ p: 3 }}>
                {toolbar}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    {error ? <Typography sx={{ color: '#64748B' }}>{t('print.notFound')}</Typography> : <CircularProgress />}
                </Box>
            </Box>
        );
    }

    const { job, company, visit } = data;
    const companyLine = [company.address, company.city].filter(Boolean).join(', ');
    const contactLine = [company.phone, company.email, company.website].filter(Boolean).join(' · ');
    const location = [
        job.municipality && `${t('jobs.fields.municipality')}: ${job.municipality}`,
        job.cadastralZone && `${t('jobs.fields.cadastralZone')}: ${job.cadastralZone}`,
        job.parcelNumber && `${t('jobs.fields.parcelNumber')}: ${job.parcelNumber}`,
    ].filter(Boolean).join(' · ');

    return (
        <Box sx={{ bgcolor: '#E5E7EB', minHeight: '100vh', py: 3, px: { xs: 1, sm: 3 } }}>
            <style>{PRINT_CSS}</style>
            {toolbar}
            <Box className="sheet" sx={{
                bgcolor: '#fff', width: '210mm', maxWidth: '100%', minHeight: '297mm', mx: 'auto', p: { xs: 3, sm: '16mm' },
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)', fontFamily: 'Montserrat, Arial, sans-serif', color: '#111827',
                boxSizing: 'border-box',
            }}>
                {/* Letterhead */}
                {/* Letterhead: the firm's logo (Company page) next to its details. */}
                <Box sx={{ borderBottom: '2px solid #427898', pb: 1.5, mb: 4, display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    {logo && <Box component="img" src={logo} alt="" sx={{ maxWidth: 140, maxHeight: 72, objectFit: 'contain', flexShrink: 0 }} />}
                    <Box sx={{ minWidth: 0 }}>
                        <Edit block sx={{ fontSize: 20, fontWeight: 800, color: '#427898' }}>{company.name}</Edit>
                        {companyLine && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{companyLine}</Edit>}
                        {contactLine && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{contactLine}</Edit>}
                        {company.taxNumber && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{`${t('print.taxNo')}: ${company.taxNumber}`}</Edit>}
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Edit block sx={{ fontSize: 19, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {kind === 'visit' ? t('print.visitTitle') : t('print.receiptTitle')}
                    </Edit>
                    {kind === 'visit' && <Edit block sx={{ fontSize: 14, color: '#4B5563', mt: 0.5 }}>{t('print.visitSubtitle')}</Edit>}
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Row label={t('print.caseNo')}>{job.number}</Row>
                    {job.cadastreCaseNumber && <Row label={t('jobs.fields.cadastreCaseNumber')}>{job.cadastreCaseNumber}</Row>}
                    <Row label={t('print.client')}>{job.client.name}</Row>
                    <Row label={t('print.job')}>{job.title}</Row>
                    <Row label={t('location.title')}>{location}</Row>
                    {kind === 'visit' && <Row label={t('print.visitDate')}>{fmtDate(visit.visitDate)}</Row>}
                </Box>

                {kind === 'visit' && (
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={{ fontSize: 13, color: '#4B5563', mb: 1 }}>{t('print.workDone')}</Typography>
                        <Edit block sx={{ minHeight: 110, border: '1px solid #D1D5DB', borderRadius: '4px', p: 1.5, fontSize: 13.5, lineHeight: 1.6 }}>
                            {visit.description || ' '}
                        </Edit>
                    </Box>
                )}

                <Edit block sx={{ fontSize: 14, lineHeight: 1.7, mb: 6 }}>
                    {kind === 'visit'
                        ? t('print.visitStatement', { date: fmtDate(visit.visitDate), company: company.name, interpolation: { escapeValue: false } })
                        : t('print.receiptStatement', { client: job.client.name, company: company.name, title: job.title, interpolation: { escapeValue: false } })}
                </Edit>

                <Box sx={{ display: 'flex', gap: 2, mb: 7, fontSize: 13.5 }}>
                    <Box sx={{ color: '#4B5563' }}>{kind === 'visit' ? t('print.placeDate') : t('print.receivedOn')}:</Box>
                    <Box sx={{ flex: 1, maxWidth: 260, borderBottom: '1px solid #111827' }}><Edit>{' '}</Edit></Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <SignLine label={t('print.signClient')} />
                    <SignLine label={t('print.signSurveyor')} />
                </Box>
            </Box>
        </Box>
    );
}
